import { sendIntegrationParamsOnReady } from './eventHandlers/sendIntegrationParamsOnReady'
import { createEmbedChannel, EmbedChannel } from './channel'
import { authorize } from './utils/authorize'
import { createIframeWithLoader } from './utils/createIframeWithLoader'
import { getXundUrls } from './utils/getXundUrls'
import { displayError } from './utils/displayError'
import { AppInstance, AppParams, ReportData } from './interfaces'
import { sendChallengeOnCheckOrigin } from './eventHandlers/sendChallengeOnCheckOrigin'

export const createApp = async (params: AppParams, previousAppInstance?: AppInstance): Promise<AppInstance> => {
  const { webappBaseUrl, authBaseUrl } = getXundUrls(params)
  const { clientId, authCode, state } = params

  const container = getTargetContainer(params.targetContainerId)
  const { iframe, loader } = createIframeWithLoader(container)

  const usedAuthCode = authCode ?? (await getAuthCode(authBaseUrl, iframe, clientId, state))

  loader?.remove()

  const channel = createEmbedChannel({ sendTo: iframe.contentWindow as Window, origin: webappBaseUrl })
  sendIntegrationParamsOnReady(channel, params, usedAuthCode, clientId)
  sendChallengeOnCheckOrigin(channel)
  setAppRestartOnRefresh()
  subscribeToReportShown(channel, params.customization?.onReportShown)

  channel.subscribe('check-started', ({ checkId }) => {
    params.customization?.onCheckStart?.({ checkId })
  })

  const appInstance = {
    getReportData: () => createReportDataPromise(channel),
    destroy,
  }

  iframe.src = webappBaseUrl

  keepPreviousAppReference(previousAppInstance, appInstance)

  function destroy() {
    channel.destroy()
    iframe.remove()
  }

  function setAppRestartOnRefresh() {
    channel.subscribe('refresh', () => {
      destroy()
      createApp(params, appInstance)
    })
  }

  return appInstance
}

const getTargetContainer = (targetContainerId: string) => {
  const container = document.getElementById(targetContainerId)
  if (!container) {
    throw new Error('Container element not found')
  }
  return container
}

const getAuthCode = async (
  authBaseUrl: string,
  container: HTMLElement,
  clientId: string,
  state: string | undefined,
) => {
  try {
    return await authorize({ url: authBaseUrl, clientId, state })
  } catch (e) {
    displayError(container as HTMLElement, e instanceof Error ? e.message : String(e))
    throw e
  }
}

const REPORT_DATA_TIMEOUT_MS = 1000 // 1 second
const createReportDataPromise = (channel: EmbedChannel) =>
  new Promise<ReportData | undefined>((resolve, reject) => {
    const timeout = setTimeout(() => {
      channel.unsubscribe('report-data', listener)
      reject(new Error('Timeout waiting for report data'))
    }, REPORT_DATA_TIMEOUT_MS)

    const listener = (data: { reportData: ReportData | undefined }) => {
      clearTimeout(timeout)
      channel.unsubscribe('report-data', listener)
      resolve(data.reportData)
    }
    channel.subscribe('report-data', listener)
    channel.send('request-report-data', {})
  })

const keepPreviousAppReference = (previousAppInstance: AppInstance | undefined, appInstance: AppInstance) => {
  if (previousAppInstance) Object.assign(previousAppInstance, appInstance)
}

function subscribeToReportShown(
  channel: EmbedChannel,
  onReportShown: ((event: { checkId: string; reportData: ReportData }) => void) | undefined,
) {
  channel.subscribe('report-shown', ({ checkId, reportData }) => {
    onReportShown?.({ checkId, reportData })
  })
}
